
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LoaderCircle, Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { isBrowser } from "@/utils/runtime";
import { supabase } from "@/integrations/supabase/client";
import { emailSchema, phoneSchema, nameSchema, messageSchema } from "@/utils/validation";
import { sanitizeText } from "@/utils/sanitizer";

const formSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  subject: z
    .string()
    .trim()
    .min(5, "Assunto precisa ter pelo menos 5 caracteres")
    .max(200, "Assunto deve ter no máximo 200 caracteres"),
  message: messageSchema
});

type FormData = z.infer<typeof formSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: ""
    }
  });

  const handleSubmit = async (data: FormData) => {
    if (!isBrowser) return;
    setIsSubmitting(true);
    
    try {
      // Sanitize all input data
      const sanitizedData = {
        name: sanitizeText(data.name.trim()),
        email: sanitizeText(data.email.trim().toLowerCase()),
        phone: data.phone ? sanitizeText(data.phone.trim()) : null,
        message: sanitizeText(`${data.subject}\n\n${data.message}`.trim()),
        source: 'Página de Contato - Formulário Completo'
      };

      const payload = {
        ...sanitizedData,
        honeypot: "",
      };

      // Store lead in database
      const { error: dbError } = await supabase
        .from('leads')
        .insert([payload]);

      if (dbError) {
        throw dbError;
      }
      
      // Show success message
      toast.success("Mensagem enviada com sucesso!", {
        description: "Entraremos em contato em breve."
      });
      
      // Reset form
      form.reset();
      
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      const errorMessage = error instanceof Error ? error.message.toLowerCase() : "";
      const isRateLimited = errorMessage.includes('too many submissions');
      const isDuplicate = errorMessage.includes('duplicate key value');
      const description =
        isRateLimited || isDuplicate
          ? "Não foi possível enviar agora. Tente novamente em alguns minutos."
          : "Por favor, tente novamente mais tarde.";

      toast.error("Erro ao enviar mensagem", {
        description,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-16">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Formulário */}
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-[#323031] mb-4">Contato</h1>
              <p className="text-lg text-[#5B5A59]">
                Entre em contato com nossa equipe para tirar dúvidas, solicitar demonstrações ou obter suporte.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-lg">
              <h2 className="text-2xl font-semibold text-[#323031] mb-6">Envie sua mensagem</h2>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <FormField 
                    control={form.control} 
                    name="name" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#323031] font-medium">Nome completo</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Digite seu nome"
                            aria-label="Digite seu nome completo"
                            className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-3 px-4 text-[#323031] placeholder:text-[#8B8680]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )} 
                  />

                  <FormField 
                    control={form.control} 
                    name="email" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#323031] font-medium">E-mail</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="seu@email.com"
                            aria-label="Digite seu e-mail"
                            className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-3 px-4 text-[#323031] placeholder:text-[#8B8680]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )} 
                  />

                  <FormField 
                    control={form.control} 
                    name="phone" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#323031] font-medium">Telefone (opcional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="(00) 00000-0000"
                            aria-label="Digite seu telefone"
                            className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-3 px-4 text-[#323031] placeholder:text-[#8B8680]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )} 
                  />

                  <FormField 
                    control={form.control} 
                    name="subject" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#323031] font-medium">Assunto</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Sobre o que você quer falar?"
                            aria-label="Digite o assunto"
                            className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-3 px-4 text-[#323031] placeholder:text-[#8B8680]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )} 
                  />

                  <FormField 
                    control={form.control} 
                    name="message" 
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#323031] font-medium">Mensagem</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Digite sua mensagem aqui..."
                            aria-label="Digite sua mensagem"
                            className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-3 px-4 text-[#323031] placeholder:text-[#8B8680] min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )} 
                  />

                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF5605] hover:bg-[#e44e05] text-white font-semibold py-4 rounded-lg text-lg transition-all duration-200 shadow-lg hover:shadow-xl" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                        Enviando mensagem...
                      </>
                    ) : (
                      "Enviar mensagem"
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>

          {/* Informações de contato */}
          <div className="lg:pl-8">
            <h2 className="text-2xl font-semibold text-[#323031] mb-8">Informações de contato</h2>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-[#FF5605]" role="img" aria-label="ícone de e-mail Toc Toc" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#323031] mb-2">Email</h3>
                  <a href="mailto:contato@toctocbr.com" className="text-[#3332FC] hover:underline text-lg">
                    contato@toctocbr.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-[#FF5605]" role="img" aria-label="ícone de telefone Toc Toc" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#323031] mb-2">Telefone</h3>
                  <a href="tel:+5547997367396" className="text-[#323031] text-lg">
                    (47) 9736-7396
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-[#FF5605]" role="img" aria-label="ícone de endereço Toc Toc" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#323031] mb-2">Endereço</h3>
                  <p className="text-[#323031] text-lg leading-relaxed">
                    Avenida Prefeito Osmar Cunha, 416 – Sala 1108<br />
                    Florianópolis – SC, 88015-100
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="h-6 w-6 text-[#FF5605]" role="img" aria-label="ícone de horário Toc Toc" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#323031] mb-2">Horário de atendimento</h3>
                  <div className="text-[#323031] text-lg">
                    <p className="mb-1">Segunda a sexta: 9h às 18h</p>
                    <p className="mb-3">Sábado: 9h às 13h</p>
                    <p className="text-sm italic text-[#5B5A59]">
                      Trabalhamos de segunda a sexta a princípio, demais horários<br />
                      com disponibilidade dos toctoques (fotógrafos).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-[#FF5605]/5 rounded-2xl border border-[#FF5605]/10">
              <h3 className="text-lg font-semibold text-[#323031] mb-2">Informações da empresa</h3>
              <div className="text-[#323031]">
                <p className="mb-1">Toc Toc Tecnologia LTDA</p>
                <p className="text-sm text-[#5B5A59]">CNPJ: 55.170.984/0001-40</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
